import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    X,
    Users,
    Calendar,
    Star,
    DollarSign,
    CheckCircle,
    User,
    Briefcase,
    Clock,
    ChevronRight,
    Sparkles,
    GraduationCap,
    History,
    Link as LinkIcon,
    MapPin,
    RefreshCw,
    Ban,
    FileText,
    Download,
} from "lucide-react";
import "./Traitementcandidatsmodal.css";

import { fetchCandidaturesByDemandeRequest } from "../../../../redux/actions/candidaturesActions";
import {
    fetchEntretiensByCandidatureRequest,
    createEntretienRequest,
    updateEntretienRequest,
    sendInterviewCalendarRequest,
} from "../../../../redux/actions/entretiensActions";
import { getAllUsersRequest } from "../../../../redux/actions/userActions";
import {
    createOffreSalarialeRequest,
    fetchOffreSalarialeByCandidatureRequest,
    updateOffreSalarialeRequest,
} from "../../../../redux/actions/offresSalarialesActions";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";


const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const NIVEAU_ETUDES_LABELS = {
    DIPLOME_BAC_2: "Diplôme (Bac + 2)",
    LICENCE_BAC_3: "Licence (Bac + 3)",
    MASTER_BAC_5: "Master (Bac + 5)",
    INGENIEUR_BAC_5: "Ingénieur (Bac + 5)",
    DOCTORAT_ET_PLUS: "Doctorat et plus",
};

const normalizeStatus = (value) => String(value || "").trim().toLowerCase();

const normalizeDecisionFinale = (value) =>
    String(value || "")
        .trim()
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const isDecisionFinaleValide = (candidature) =>
    ["VALIDE", "VALIDEE"].includes(normalizeDecisionFinale(candidature?.decisionFinale));

const isDecisionFinaleRefusee = (candidature) =>
    ["REFUSE", "REFUSEE", "A_REVOIR", "REJETE", "REJETEE"].includes(normalizeDecisionFinale(candidature?.decisionFinale));

const getOffreSalarialeStatut = (candidature) =>
    candidature?.offreSalariale?.statutOffre || candidature?.statutOffre || "";

const isMissingStatut = (value) => {
    const normalized = String(value || "").trim();
    return !normalized || normalized === "-";
};

const getDisplayedPoste = (c) => {
    if (c?.autrePoste?.trim()) return c.autrePoste.trim();
    return c?.posteNom || c?.poste || "—";
};

const getDisplayedNiveauEtude = (c) => {
    const raw = c?.niveauEtude;
    return NIVEAU_ETUDES_LABELS[raw] || raw || "—";
};

const getCandidatCvId = (candidat) => candidat?.candidatId || candidat?.candidatTheque?.id || null;

const getCandidatCvName = (candidat) =>
    candidat?.cvDocument ||
    candidat?.candidatTheque?.cvDocument ||
    candidat?.candidatTheque?.cv ||
    "";

const normalizeApiBase = () => {
    const base = String(API_BASE_URL || "").replace(/\/$/, "");
    return /\/api$/i.test(base) ? base : `${base}/api`;
};

const getFilenameFromContentDisposition = (header) => {
    const match = String(header || "").match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
    return match ? decodeURIComponent(match[1].replace(/"/g, "")) : "";
};

const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("fr-FR");
};

const formatMode = (mode) => {
    if (mode === "EN_LIGNE") return "En ligne";
    if (mode === "PRESENTIEL") return "Présentiel";
    return mode || "—";
};

const getEntretienTypeLabel = (type) => {
    if (type === "TECHNIQUE") return "Entretien Technique";
    if (type === "HYBRIDE") return "Entretien Hybride";
    return "Entretien RH";
};

const getEntretienQuestionFocus = (type) => {
    if (type === "TECHNIQUE") return "techniques";
    if (type === "HYBRIDE") return "techniques et RH";
    return "RH";
};

const getUserDisplayNameById = (users, userId) => {
    if (!userId) return "—";

    const user = (users || []).find((u) => Number(u.id) === Number(userId));
    if (!user) return `ID ${userId}`;

    if (user.fullName && String(user.fullName).trim()) {
        return user.fullName;
    }

    const full = `${user.firstName || ""} ${user.lastName || ""}`.trim();
    return full || `ID ${userId}`;
};

const getDemandeurDisplay = (users, demande) => {
    return getUserDisplayNameById(users, demande?.demandeurId);
};

const getPlanificateurDisplay = (users, entretien) => {
    return getUserDisplayNameById(users, entretien?.createdBy);
};

const buildInterviewParticipantModes = (candidature, users, currentUserId, entretien = null) => {
    const modes = {};

    (entretien?.invitationParticipants || []).forEach((participant) => {
        if (participant?.userId) {
            modes[participant.userId] = participant.recipientType || "CC";
        }
    });

    if (!entretien) {
        if (currentUserId) {
            modes[currentUserId] = "CC";
        }
        if (candidature?.demandeurId) {
            modes[candidature.demandeurId] = modes[candidature.demandeurId] || "CC";
        }
    }

    const availableIds = new Set((users || []).map((user) => Number(user.id)));
    Object.keys(modes).forEach((key) => {
        if (!availableIds.has(Number(key))) {
            delete modes[key];
        }
    });

    return modes;
};

const extractParticipantPayload = (participantModes) => {
    const toParticipantIds = [];
    const ccParticipantIds = [];

    Object.entries(participantModes || {}).forEach(([userId, mode]) => {
        if (mode === "TO") {
            toParticipantIds.push(Number(userId));
        }
        if (mode === "CC") {
            ccParticipantIds.push(Number(userId));
        }
    });

    return { toParticipantIds, ccParticipantIds };
};

/* =============================================
   SUB-MODAL : Historique des entretiens
============================================= */
function HistoriqueEntretiensModal({ open, onClose, candidat, entretiens, users, onCalendarAction }) {
    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <History size={17} />
                        Historique des entretiens —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    {!entretiens || entretiens.length === 0 ? (
                        <div className="tcmEmptyCell">Aucun entretien planifié</div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {entretiens.map((e) => (
                                <div
                                    key={e.id}
                                    style={{
                                        border: "1px solid #e5e7eb",
                                        borderRadius: 8,
                                        padding: 12,
                                        background: "#f9fafb",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 12,
                                            flexWrap: "wrap",
                                        }}
                                    >
                                        <div style={{ fontWeight: 600 }}>
                                            {getEntretienTypeLabel(e.type)}
                                        </div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                                            Planifié par : {getPlanificateurDisplay(users, e)}
                                        </div>
                                    </div>

                                    <div style={{ marginTop: 8, fontSize: 13, color: "#374151" }}>
                                        <div><strong>Mode :</strong> {formatMode(e.mode)}</div>
                                        <div>
                                            <strong>{e.mode === "EN_LIGNE" ? "Lien visio" : "Lieu"} :</strong>{" "}
                                            {e.lieu || "—"}
                                        </div>
                                        <div><strong>Date :</strong> {e.date ? formatDate(e.date) : "—"}</div>
                                        <div><strong>Heure :</strong> {e.heure || "—"}</div>
                                        <div><strong>Statut :</strong> {e.statut || "PLANIFIE"}</div>
                                        <div><strong>Commentaire :</strong> {e.commentaire || "—"}</div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
                                        {String(e.statut || "").toUpperCase() !== "ANNULE" && (
                                            <button
                                                className="tcmIconBtn tcmIconBtn--calendar"
                                                title="Replanifier"
                                                type="button"
                                                onClick={() => onCalendarAction(e, "RESCHEDULE_INTERVIEW")}
                                            >
                                                <RefreshCw size={15} />
                                            </button>
                                        )}
                                        {String(e.statut || "").toUpperCase() !== "ANNULE" && (
                                            <button
                                                className="tcmIconBtn tcmIconBtn--danger"
                                                title="Annuler"
                                                type="button"
                                                onClick={() => onCalendarAction(e, "CANCEL_INTERVIEW")}
                                            >
                                                <Ban size={15} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                        Fermer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Planifier Entretien
============================================= */
function PlanifierEntretienModal({ open, onClose, candidat, onSave, users, currentUserId }) {
    const [typeEntretien, setTypeEntretien] = useState("");
    const [dateEntretien, setDateEntretien] = useState("");
    const [heureEntretien, setHeureEntretien] = useState("");
    const [modeEntretien, setModeEntretien] = useState("");
    const [lieuEntretien, setLieuEntretien] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [step, setStep] = useState(1);
    const [participantModes, setParticipantModes] = useState({});

    const reset = () => {
        setTypeEntretien("");
        setDateEntretien("");
        setHeureEntretien("");
        setModeEntretien("");
        setLieuEntretien("");
        setCommentaire("");
        setStep(1);
        setParticipantModes({});
    };

    useEffect(() => {
        if (!open || !candidat) return;
        setParticipantModes(buildInterviewParticipantModes(candidat, users, currentUserId));
    }, [open, candidat, users, currentUserId]);

    const handleNext = () => {
        if (!typeEntretien) return;
        setStep(2);
    };

    const handleSubmit = () => {
        if (!dateEntretien || !heureEntretien || !modeEntretien || !lieuEntretien) return;
        const participantPayload = extractParticipantPayload(participantModes);

        onSave({
            type: typeEntretien,
            mode: modeEntretien,
            lieu: lieuEntretien,
            date: dateEntretien,
            heure: heureEntretien,
            dureeMinutes: 60,
            commentaire,
            ...participantPayload,
        });

        reset();
        onClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Calendar size={17} />
                        Planifier un entretien —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSteps">
                    <div className={`tcmStep ${step >= 1 ? "tcmStep--active" : ""}`}>
                        <div className="tcmStep__num">1</div>
                        <span>Type</span>
                    </div>
                    <ChevronRight size={14} className="tcmStep__sep" />
                    <div className={`tcmStep ${step >= 2 ? "tcmStep--active" : ""}`}>
                        <div className="tcmStep__num">2</div>
                        <span>Planification</span>
                    </div>
                </div>

                <div className="tcmSubModal__body">
                    {step === 1 && (
                        <div className="tcmStepContent">
                            <p className="tcmStepDesc">
                                Choisissez le type d'entretien à planifier.
                            </p>

                            <div className="tcmTypeCards">
                                <div
                                    className={`tcmTypeCard ${typeEntretien === "TECHNIQUE" ? "tcmTypeCard--selected" : ""}`}
                                    onClick={() => setTypeEntretien("TECHNIQUE")}
                                >
                                    <div className="tcmTypeCard__icon tcmTypeCard__icon--tech">
                                        <Briefcase size={22} />
                                    </div>
                                    <div className="tcmTypeCard__label">Entretien Technique</div>
                                    <div className="tcmTypeCard__desc">
                                        Évaluation des compétences techniques et métier
                                    </div>
                                    {typeEntretien === "TECHNIQUE" && (
                                        <div className="tcmTypeCard__check">
                                            <CheckCircle size={16} />
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`tcmTypeCard ${typeEntretien === "RH" ? "tcmTypeCard--selected" : ""}`}
                                    onClick={() => setTypeEntretien("RH")}
                                >
                                    <div className="tcmTypeCard__icon tcmTypeCard__icon--rh">
                                        <Users size={22} />
                                    </div>
                                    <div className="tcmTypeCard__label">Entretien RH</div>
                                    <div className="tcmTypeCard__desc">
                                        Évaluation de la personnalité, motivation et culture fit
                                    </div>
                                    {typeEntretien === "RH" && (
                                        <div className="tcmTypeCard__check">
                                            <CheckCircle size={16} />
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`tcmTypeCard ${typeEntretien === "HYBRIDE" ? "tcmTypeCard--selected" : ""}`}
                                    onClick={() => setTypeEntretien("HYBRIDE")}
                                >
                                    <div className="tcmTypeCard__icon tcmTypeCard__icon--hybrid">
                                        <Sparkles size={22} />
                                    </div>
                                    <div className="tcmTypeCard__label">Entretien Hybride</div>
                                    <div className="tcmTypeCard__desc">
                                        Entretien combinant les volets technique et RH
                                    </div>
                                    {typeEntretien === "HYBRIDE" && (
                                        <div className="tcmTypeCard__check">
                                            <CheckCircle size={16} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="tcmStepContent">
                            <div className="tcmEntretienTypeBadge tcmEntretienTypeBadge--selected">
                                {typeEntretien === "TECHNIQUE" && (
                                    <>
                                        <Briefcase size={14} /> Entretien Technique
                                    </>
                                )}
                                {typeEntretien === "RH" && (
                                    <>
                                        <Users size={14} /> Entretien RH
                                    </>
                                )}
                                {typeEntretien === "HYBRIDE" && (
                                    <>
                                        <Sparkles size={14} /> Entretien Hybride
                                    </>
                                )}
                            </div>

                            <div className="tcmFormGrid">
                                <div className="tcmFormField tcmFormField--half">
                                    <label>Date *</label>
                                    <input
                                        type="date"
                                        value={dateEntretien}
                                        onChange={(e) => setDateEntretien(e.target.value)}
                                    />
                                </div>

                                <div className="tcmFormField tcmFormField--half">
                                    <label>Heure *</label>
                                    <input
                                        type="time"
                                        value={heureEntretien}
                                        onChange={(e) => setHeureEntretien(e.target.value)}
                                    />
                                </div>

                                <div className="tcmFormField">
                                    <label>Mode *</label>
                                    <select
                                        value={modeEntretien}
                                        onChange={(e) => {
                                            setModeEntretien(e.target.value);
                                            setLieuEntretien("");
                                        }}
                                    >
                                        <option value="">Sélectionner</option>
                                        <option value="EN_LIGNE">En ligne</option>
                                        <option value="PRESENTIEL">Présentiel</option>
                                    </select>
                                </div>

                                {modeEntretien === "EN_LIGNE" && (
                                    <div className="tcmFormField">
                                        <label>Lien visio *</label>
                                        <div className="tcmCellInline">
                                            <LinkIcon size={14} />
                                            <input
                                                type="url"
                                                placeholder="https://meet.google.com/..."
                                                value={lieuEntretien}
                                                onChange={(e) => setLieuEntretien(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                {modeEntretien === "PRESENTIEL" && (
                                    <div className="tcmFormField">
                                        <label>Adresse / salle *</label>
                                        <div className="tcmCellInline">
                                            <MapPin size={14} />
                                            <input
                                                type="text"
                                                placeholder="Ex: Salle A - 2ème étage"
                                                value={lieuEntretien}
                                                onChange={(e) => setLieuEntretien(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="tcmFormField">
                                    <label>Commentaire</label>
                                    <textarea
                                        rows={3}
                                        placeholder="Instructions particulières..."
                                        value={commentaire}
                                        onChange={(e) => setCommentaire(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="tcmFormField">
                                <label>Destinataires calendrier</label>
                                <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "2fr 1fr",
                                            padding: "10px 12px",
                                            background: "#f9fafb",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            color: "#6b7280",
                                        }}
                                    >
                                        <span>Participant</span>
                                        <span>Invitation</span>
                                    </div>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "2fr 1fr",
                                            padding: "10px 12px",
                                            borderTop: "1px solid #e5e7eb",
                                            alignItems: "center",
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600 }}>
                                                {candidat?.candidatPrenom} {candidat?.candidatNom}
                                            </div>
                                            <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                {candidat?.candidatEmail || "Email non renseigné"}
                                            </div>
                                        </div>
                                        <div style={{ fontWeight: 700, color: "#204385" }}>TO (défaut)</div>
                                    </div>
                                    {(users || []).map((user) => {
                                        const email = user.email || "";
                                        const hasEmail = String(email).trim().length > 0;

                                        return (
                                            <div
                                                key={user.id}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "2fr 1fr",
                                                    padding: "10px 12px",
                                                    borderTop: "1px solid #e5e7eb",
                                                    alignItems: "center",
                                                    opacity: hasEmail ? 1 : 0.55,
                                                }}
                                            >
                                                <div>
                                                    <div style={{ fontWeight: 600 }}>
                                                        {getUserDisplayNameById(users, user.id)}
                                                    </div>
                                                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                                                        {hasEmail ? email : "Email non renseigné"}
                                                    </div>
                                                </div>
                                                <select
                                                    value={participantModes[user.id] || "NONE"}
                                                    disabled={!hasEmail}
                                                    onChange={(e) =>
                                                        setParticipantModes((prev) => {
                                                            const next = { ...prev };
                                                            if (e.target.value === "NONE") {
                                                                delete next[user.id];
                                                            } else {
                                                                next[user.id] = e.target.value;
                                                            }
                                                            return next;
                                                        })
                                                    }
                                                >
                                                    <option value="NONE">Aucun</option>
                                                    <option value="TO">TO</option>
                                                    <option value="CC">CC</option>
                                                </select>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className="tcmSubModal__footer">
                    <div className="tcmFooterRight">
                        {step === 2 && (
                            <button className="tcmBtn tcmBtn--ghost" onClick={() => setStep(1)} type="button">
                                Retour
                            </button>
                        )}

                        <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                            Annuler
                        </button>

                        {step === 1 ? (
                            <button
                                className="tcmBtn tcmBtn--primary"
                                onClick={handleNext}
                                disabled={!typeEntretien}
                                type="button"
                            >
                                Suivant
                            </button>
                        ) : (
                            <button
                                className="tcmBtn tcmBtn--primary"
                                onClick={handleSubmit}
                                disabled={!dateEntretien || !heureEntretien || !modeEntretien || !lieuEntretien}
                                type="button"
                            >
                                <Calendar size={15} /> Planifier
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Plan d'entretien IA
============================================= */
function PlanEntretienIAModal({ open, onClose, candidat }) {
    const [typeEntretien, setTypeEntretien] = useState("");
    const [generatingPlan, setGeneratingPlan] = useState(false);
    const [planIA, setPlanIA] = useState("");

    const reset = () => {
        setTypeEntretien("");
        setGeneratingPlan(false);
        setPlanIA("");
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    const handleGeneratePlanIA = async () => {
        if (!typeEntretien || !candidat) return;

        setGeneratingPlan(true);
        try {
            await new Promise((r) => setTimeout(r, 700));
            const who = `${candidat.candidatPrenom} ${candidat.candidatNom}`;
            const role = getDisplayedPoste(candidat);

            setPlanIA(
                `Plan d'entretien (${typeEntretien}) — ${who}\n` +
                `Poste : ${role}\n\n` +
                `1) Présentation du contexte\n` +
                `2) Parcours du candidat\n` +
                `3) Questions ciblées ${getEntretienQuestionFocus(typeEntretien)}\n` +
                `4) Disponibilité / motivation\n` +
                `5) Questions-réponses et clôture`
            );
        } finally {
            setGeneratingPlan(false);
        }
    };

    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Sparkles size={17} />
                        Plan d'entretien (IA) —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmStepContent">
                        <p className="tcmStepDesc">
                            Choisissez le type d'entretien puis générez le plan correspondant.
                        </p>

                        <div className="tcmTypeCards">
                            <div
                                className={`tcmTypeCard ${typeEntretien === "TECHNIQUE" ? "tcmTypeCard--selected" : ""}`}
                                onClick={() => setTypeEntretien("TECHNIQUE")}
                            >
                                <div className="tcmTypeCard__icon tcmTypeCard__icon--tech">
                                    <Briefcase size={22} />
                                </div>
                                <div className="tcmTypeCard__label">Entretien Technique</div>
                                <div className="tcmTypeCard__desc">
                                    Générer un plan ciblé sur les compétences techniques et métier
                                </div>
                                {typeEntretien === "TECHNIQUE" && (
                                    <div className="tcmTypeCard__check">
                                        <CheckCircle size={16} />
                                    </div>
                                )}
                            </div>

                            <div
                                className={`tcmTypeCard ${typeEntretien === "RH" ? "tcmTypeCard--selected" : ""}`}
                                onClick={() => setTypeEntretien("RH")}
                            >
                                <div className="tcmTypeCard__icon tcmTypeCard__icon--rh">
                                    <Users size={22} />
                                </div>
                                <div className="tcmTypeCard__label">Entretien RH</div>
                                <div className="tcmTypeCard__desc">
                                    Générer un plan ciblé sur la motivation, le comportement et le fit culturel
                                </div>
                                {typeEntretien === "RH" && (
                                    <div className="tcmTypeCard__check">
                                        <CheckCircle size={16} />
                                    </div>
                                )}
                            </div>

                            <div
                                className={`tcmTypeCard ${typeEntretien === "HYBRIDE" ? "tcmTypeCard--selected" : ""}`}
                                onClick={() => setTypeEntretien("HYBRIDE")}
                            >
                                <div className="tcmTypeCard__icon tcmTypeCard__icon--hybrid">
                                    <Sparkles size={22} />
                                </div>
                                <div className="tcmTypeCard__label">Entretien Hybride</div>
                                <div className="tcmTypeCard__desc">
                                    Générer un plan mixte technique et RH
                                </div>
                                {typeEntretien === "HYBRIDE" && (
                                    <div className="tcmTypeCard__check">
                                        <CheckCircle size={16} />
                                    </div>
                                )}
                            </div>
                        </div>

                        {planIA && (
                            <div className="tcmAiPlanBox">
                                <div className="tcmAiPlanBox__head">
                                    <Sparkles size={16} />
                                    <span>Plan d'entretien généré (IA)</span>
                                </div>
                                <pre className="tcmAiPlanBox__content">{planIA}</pre>
                            </div>
                        )}
                    </div>
                </div>

                <div className="tcmSubModal__footer tcmSubModal__footer--split">
                    <div className="tcmFooterLeft">
                        <button
                            className="tcmBtn tcmBtn--ghost tcmBtn--ai"
                            type="button"
                            onClick={handleGeneratePlanIA}
                            disabled={!typeEntretien || generatingPlan}
                        >
                            <Sparkles size={15} />
                            {generatingPlan ? "Génération..." : "Générer le plan"}
                        </button>
                    </div>

                    <div className="tcmFooterRight">
                        <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Notes d'entretien
============================================= */
function NotesEntretienModal({ open, onClose, candidat, initialData, onSave }) {
    const [pretentions, setPretentions] = useState("");
    const [disponibilite, setDisponibilite] = useState("");
    const [mobilite, setMobilite] = useState("");
    const [casSocial, setCasSocial] = useState("");
    const [motivation, setMotivation] = useState("");
    const [pointsForts, setPointsForts] = useState("");
    const [pointsFaibles, setPointsFaibles] = useState("");
    const [observations, setObservations] = useState("");

    useEffect(() => {
        if (!open) return;

        setPretentions(initialData?.pretentions || "");
        setDisponibilite(initialData?.disponibilite || "");
        setMobilite(initialData?.mobilite || "");
        setCasSocial(initialData?.casSocial || "");
        setMotivation(initialData?.motivation || "");
        setPointsForts(initialData?.pointsForts || "");
        setPointsFaibles(initialData?.pointsFaibles || "");
        setObservations(initialData?.observations || "");
    }, [open, initialData]);

    const reset = () => {
        setPretentions("");
        setDisponibilite("");
        setMobilite("");
        setCasSocial("");
        setMotivation("");
        setPointsForts("");
        setPointsFaibles("");
        setObservations("");
    };

    const handleSubmit = () => {
        onSave({
            pretentions,
            disponibilite,
            mobilite,
            casSocial,
            motivation,
            pointsForts,
            pointsFaibles,
            observations,
        });
        reset();
        onClose();
    };

    const handleClose = () => {
        reset();
        onClose();
    };

    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <FileText size={17} />
                        Notes d'entretien —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmFormGrid">
                        <div className="tcmFormField">
                            <label>Prétentions salariales</label>
                            <input
                                type="text"
                                placeholder="Ex: 12 000 MAD"
                                value={pretentions}
                                onChange={(e) => setPretentions(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Disponibilité</label>
                            <input
                                type="text"
                                placeholder="Ex: Immédiate / 1 mois"
                                value={disponibilite}
                                onChange={(e) => setDisponibilite(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Mobilité</label>
                            <select value={mobilite} onChange={(e) => setMobilite(e.target.value)}>
                                <option value="">Sélectionner</option>
                                <option value="Nationale">Nationale</option>
                                <option value="Internationale">Internationale</option>
                                <option value="Les deux">Les deux</option>
                                <option value="Aucune">Aucune</option>
                            </select>
                        </div>

                        <div className="tcmFormField">
                            <label>Cas social</label>
                            <textarea
                                rows={2}
                                placeholder="Informations personnelles importantes..."
                                value={casSocial}
                                onChange={(e) => setCasSocial(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Motivation</label>
                            <textarea
                                rows={2}
                                placeholder="Motivation générale du candidat..."
                                value={motivation}
                                onChange={(e) => setMotivation(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Points forts</label>
                            <textarea
                                rows={2}
                                placeholder="Compétences, attitude, communication..."
                                value={pointsForts}
                                onChange={(e) => setPointsForts(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Points faibles</label>
                            <textarea
                                rows={2}
                                placeholder="Axes d'amélioration, réserves..."
                                value={pointsFaibles}
                                onChange={(e) => setPointsFaibles(e.target.value)}
                            />
                        </div>

                        <div className="tcmFormField">
                            <label>Observations générales</label>
                            <textarea
                                rows={3}
                                placeholder="Observations RH, comportement, fit, etc."
                                value={observations}
                                onChange={(e) => setObservations(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button className="tcmBtn tcmBtn--primary" onClick={handleSubmit} type="button">
                        <FileText size={15} /> Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Évaluer Candidat
============================================= */
function EvaluerModal({ open, onClose, candidat, onSave }) {
    const [note, setNote] = useState(0);
    const [hoverNote, setHoverNote] = useState(0);
    const [commentaire, setCommentaire] = useState("");
    const [decision, setDecision] = useState("");
    const [competences, setCompetences] = useState({
        technique: 0,
        communication: 0,
        aptitude: 0,
        motivation: 0,
    });

    const handleSubmit = () => {
        if (!note || !decision) return;
        onSave({ note, commentaire, decision, competences });
        setNote(0);
        setCommentaire("");
        setDecision("");
        setCompetences({ technique: 0, communication: 0, aptitude: 0, motivation: 0 });
        onClose();
    };

    const handleClose = () => {
        setNote(0);
        setHoverNote(0);
        setCommentaire("");
        setDecision("");
        setCompetences({ technique: 0, communication: 0, aptitude: 0, motivation: 0 });
        onClose();
    };

    if (!open || !candidat) return null;

    const criterias = [
        { key: "technique", label: "Compétences techniques" },
        { key: "communication", label: "Communication" },
        { key: "aptitude", label: "Aptitude au poste" },
        { key: "motivation", label: "Motivation" },
    ];

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Star size={17} />
                        Évaluation —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Note globale *</div>
                        <div className="tcmStars">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    className={`tcmStar ${(hoverNote || note) >= star ? "tcmStar--filled" : ""}`}
                                    onMouseEnter={() => setHoverNote(star)}
                                    onMouseLeave={() => setHoverNote(0)}
                                    onClick={() => setNote(star)}
                                >
                                    <Star size={28} />
                                </button>
                            ))}
                            {note > 0 && (
                                <span className="tcmStarLabel">
                                    {["", "Insuffisant", "Passable", "Bien", "Très bien", "Excellent"][note]}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Évaluation par critères</div>
                        <div className="tcmCriterias">
                            {criterias.map((c) => (
                                <div key={c.key} className="tcmCriteria">
                                    <div className="tcmCriteria__label">{c.label}</div>
                                    <div className="tcmCriteria__stars">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                className={`tcmStarSm ${
                                                    competences[c.key] >= star ? "tcmStarSm--filled" : ""
                                                }`}
                                                onClick={() =>
                                                    setCompetences((prev) => ({ ...prev, [c.key]: star }))
                                                }
                                            >
                                                <Star size={18} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Commentaire</div>
                        <textarea
                            className="tcmTextarea"
                            rows={3}
                            placeholder="Points forts, points d'amélioration, observations..."
                            value={commentaire}
                            onChange={(e) => setCommentaire(e.target.value)}
                        />
                    </div>

                    <div className="tcmEvalSection">
                        <div className="tcmEvalSectionTitle">Décision *</div>
                        <div className="tcmDecisions">
                            {[
                                { value: "Retenu", color: "#4CAF50", label: "✅ Retenu" },
                                { value: "En réserve", color: "#ff9800", label: "⏳ En réserve" },
                                { value: "Refusé", color: "#f44336", label: "❌ Refusé" },
                            ].map((d) => (
                                <div
                                    key={d.value}
                                    className={`tcmDecisionCard ${
                                        decision === d.value ? "tcmDecisionCard--selected" : ""
                                    }`}
                                    style={
                                        decision === d.value
                                            ? { borderColor: d.color, background: `${d.color}12` }
                                            : {}
                                    }
                                    onClick={() => setDecision(d.value)}
                                >
                                    {d.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={handleSubmit}
                        disabled={!note || !decision}
                        type="button"
                    >
                        <Star size={15} /> Enregistrer l'évaluation
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Offre Salariale
============================================= */
function OffreSalarialeModal({ open, onClose, candidat, existingOffre, loading, onSave }) {
    const [salaireBase, setSalaireBase] = useState("");
    const [primes, setPrimes] = useState("");
    const [avantages, setAvantages] = useState("");
    const [dateDebut, setDateDebut] = useState("");
    const [periodeEssai, setPeriodeEssai] = useState("");
    const [noteComplementaire, setNoteComplementaire] = useState("");
    const [envoyerEmail, setEnvoyerEmail] = useState(true);
    const [statutOffre, setStatutOffre] = useState("");

    useEffect(() => {
        if (!open || !candidat) return;
        const offre = existingOffre || candidat.offreSalariale || {};
        setSalaireBase(offre.salaireBase ?? offre.salaire ?? "");
        setPrimes(offre.primes ?? offre.primeBonus ?? "");
        setAvantages(offre.avantages || "");
        setDateDebut(offre.dateDebut || offre.datePrisePoste || "");
        setPeriodeEssai(offre.periodeEssai || "");
        setNoteComplementaire(offre.noteComplementaire ?? offre.commentaire ?? "");
        setEnvoyerEmail(offre.envoyerEmail ?? true);
        setStatutOffre(offre.statutOffre || candidat.statutOffre || "");
    }, [open, candidat, existingOffre]);

    const resetForm = () => {
        setSalaireBase("");
        setPrimes("");
        setAvantages("");
        setDateDebut("");
        setPeriodeEssai("");
        setNoteComplementaire("");
        setEnvoyerEmail(true);
        setStatutOffre("");
    };

    const handleSubmit = (nextStatutOffre = statutOffre, shouldSendEmail = envoyerEmail) => {
        if (!salaireBase) {
            showSwal({
                icon: "warning",
                text: "Veuillez remplir le salaire.",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }
        onSave({
            salaireBase,
            primes,
            avantages,
            dateDebut,
            periodeEssai,
            noteComplementaire,
            envoyerEmail: shouldSendEmail,
            statutOffre: shouldSendEmail && isMissingStatut(nextStatutOffre) ? "ENVOYE" : nextStatutOffre,
        });
        resetForm();
        onClose();
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!open || !candidat) return null;

    const isOffreEnvoyee = Boolean(existingOffre?.id || candidat.offreSalariale?.id);
    const isOffreAcceptee = statutOffre === "ACCEPTEE";
    const isOffreRefusee = statutOffre === "REFUSEE";

    return (
        <div className="tcmSubModal" onMouseDown={handleClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <DollarSign size={17} />
                        Offre salariale —{" "}
                        <span className="tcmSubModal__titleName">
                            {candidat.candidatPrenom} {candidat.candidatNom}
                        </span>
                    </div>
                    <button className="tcmIconBtn" onClick={handleClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmOffrePreview">
                        <div className="tcmOffrePreview__icon">
                            <DollarSign size={20} />
                        </div>
                        <div>
                            <div className="tcmOffrePreview__name">
                                {candidat.candidatPrenom} {candidat.candidatNom}
                            </div>
                            <div className="tcmOffrePreview__post">{getDisplayedPoste(candidat)}</div>
                            <div className="tcmOffrePreview__email">{candidat.candidatEmail}</div>
                            <div className="tcmOffrePreview__status">
                                Statut offre : {statutOffre === "ACCEPTEE" ? "Accepté" : statutOffre === "REFUSEE" ? "Refusé" : statutOffre === "ENVOYE" ? "Envoyé" : "—"}
                            </div>
                        </div>
                    </div>

                    <div className="tcmFormGrid">
                        <div className="tcmFormField tcmFormField--half">
                            <label>Salaire de base brut (MAD/mois) *</label>
                            <input
                                type="number"
                                placeholder="Ex: 12000"
                                value={salaireBase}
                                onChange={(e) => setSalaireBase(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField tcmFormField--half">
                            <label>Primes et bonus (MAD/an)</label>
                            <input
                                type="number"
                                placeholder="Ex: 24000"
                                value={primes}
                                onChange={(e) => setPrimes(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField">
                            <label>Avantages</label>
                            <input
                                type="text"
                                placeholder="Ex: Mutuelle, voiture de fonction..."
                                value={avantages}
                                onChange={(e) => setAvantages(e.target.value)}
                            />
                        </div>
                        {/*<div className="tcmFormField tcmFormField--half">*/}
                        {/*    <label>Date de prise de poste</label>*/}
                        {/*    <input*/}
                        {/*        type="date"*/}
                        {/*        value={dateDebut}*/}
                        {/*        onChange={(e) => setDateDebut(e.target.value)}*/}
                        {/*    />*/}
                        {/*</div>*/}
                        <div className="tcmFormField tcmFormField--half">
                            <label>Période d'essai</label>
                            <select value={periodeEssai} onChange={(e) => setPeriodeEssai(e.target.value)}>
                                <option value="">Sélectionner...</option>
                                <option value="1 mois">1 mois</option>
                                <option value="2 mois">2 mois</option>
                                <option value="3 mois">3 mois</option>
                                <option value="6 mois">6 mois</option>
                            </select>
                        </div>
                        <div className="tcmFormField">
                            <label>Note complémentaire</label>
                            <textarea
                                rows={3}
                                placeholder="Conditions particulières..."
                                value={noteComplementaire}
                                onChange={(e) => setNoteComplementaire(e.target.value)}
                            />
                        </div>
                        <div className="tcmFormField">
                            <div className="tcmCheckRow">
                                <input
                                    type="checkbox"
                                    id="sendEmail"
                                    checked={envoyerEmail}
                                    onChange={(e) => setEnvoyerEmail(e.target.checked)}
                                />
                                <label htmlFor="sendEmail" className="tcmCheckLabel">
                                    Envoyer l'offre par email au candidat ({candidat.candidatEmail})
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={handleClose} type="button">
                        Annuler
                    </button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={() => handleSubmit()}
                        disabled={loading || isOffreEnvoyee}
                        type="button"
                    >
                        <DollarSign size={15} />
                        {isOffreEnvoyee ? "Offre envoyée" : envoyerEmail ? "Envoyer l'offre" : "Enregistrer l'offre"}
                    </button>
                    {!isOffreAcceptee && !isOffreRefusee && (
                        <>
                            <button
                                className="tcmBtn tcmBtn--success"
                                onClick={() => handleSubmit("ACCEPTEE", false)}
                                disabled={loading}
                                type="button"
                            >
                                <CheckCircle size={15} />
                                Accepter
                            </button>
                            <button
                                className="tcmBtn tcmBtn--danger"
                                onClick={() => handleSubmit("REFUSEE", false)}
                                disabled={loading}
                                type="button"
                            >
                                <Ban size={15} />
                                Refuser
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

/* =============================================
   SUB-MODAL : Confirmer Terminé
============================================= */
function TerminerModal({ open, onClose, candidat, onConfirm }) {
    if (!open || !candidat) return null;

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel tcmSubModal__panel--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <CheckCircle size={17} />
                        Finaliser le recrutement
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmTerminerContent">
                        <div className="tcmTerminerIcon">
                            <CheckCircle size={40} color="#4CAF50" />
                        </div>
                        <p className="tcmTerminerText">
                            Confirmez-vous que cette étape du processus de candidature de{" "}
                            <strong>
                                {candidat.candidatPrenom} {candidat.candidatNom}
                            </strong>{" "}
                            pour le poste de <strong>{getDisplayedPoste(candidat)}</strong> est validée ?
                        </p>
                        <p className="tcmTerminerSubText">
                            Cette action permettra de passer aux prochaines étapes.
                        </p>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                        Annuler
                    </button>
                    <button className="tcmBtn tcmBtn--success" onClick={onConfirm} type="button">
                        <CheckCircle size={15} /> Confirmer
                    </button>
                </div>
            </div>
        </div>
    );
}

function ReplanifierEntretienModal({ open, onClose, candidat, entretien, users, currentUserId, onSubmit, loading }) {
    const [date, setDate] = useState("");
    const [heure, setHeure] = useState("");
    const [modeEntretien, setModeEntretien] = useState("");
    const [lieuEntretien, setLieuEntretien] = useState("");
    const [commentaire, setCommentaire] = useState("");
    const [participantModes, setParticipantModes] = useState({});

    useEffect(() => {
        if (!open || !candidat || !entretien) return;
        setDate(entretien.date || "");
        setHeure(entretien.heure || "");
        setModeEntretien(entretien.mode || "");
        setLieuEntretien(entretien.lieu || "");
        setCommentaire(entretien.commentaire || "");
        setParticipantModes(buildInterviewParticipantModes(candidat, users, currentUserId, entretien));
    }, [open, candidat, entretien, users, currentUserId]);

    if (!open || !candidat || !entretien) return null;

    const handleSubmit = () => {
        const participantPayload = extractParticipantPayload(participantModes);
        onSubmit("RESCHEDULE_INTERVIEW", entretien, {
            type: entretien.type,
            mode: modeEntretien,
            lieu: lieuEntretien,
            date,
            heure,
            dureeMinutes: entretien.dureeMinutes || 60,
            commentaire,
            ...participantPayload,
        });
    };

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <RefreshCw size={17} />
                        Replanifier l'entretien
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmFormGrid">
                        <div className="tcmFormField tcmFormField--half">
                            <label>Date *</label>
                            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
                        </div>
                        <div className="tcmFormField tcmFormField--half">
                            <label>Heure *</label>
                            <input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} />
                        </div>
                        <div className="tcmFormField">
                            <label>Mode *</label>
                            <select value={modeEntretien} onChange={(e) => setModeEntretien(e.target.value)}>
                                <option value="">Sélectionner</option>
                                <option value="EN_LIGNE">En ligne</option>
                                <option value="PRESENTIEL">Présentiel</option>
                            </select>
                        </div>
                        <div className="tcmFormField">
                            <label>{modeEntretien === "EN_LIGNE" ? "Lien visio *" : "Adresse / salle *"}</label>
                            <input value={lieuEntretien} onChange={(e) => setLieuEntretien(e.target.value)} />
                        </div>
                        <div className="tcmFormField">
                            <label>Commentaire</label>
                            <textarea rows={3} value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
                        </div>
                        <div className="tcmFormField">
                            <label>Destinataires calendrier</label>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", background: "#f9fafb", fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                                    <span>Participant</span>
                                    <span>Invitation</span>
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center" }}>
                                    <div>
                                        <div style={{ fontWeight: 600 }}>{candidat.candidatPrenom} {candidat.candidatNom}</div>
                                        <div style={{ fontSize: 12, color: "#6b7280" }}>{candidat.candidatEmail || "Email non renseigné"}</div>
                                    </div>
                                    <div style={{ fontWeight: 700, color: "#204385" }}>TO (défaut)</div>
                                </div>
                                {(users || []).map((user) => {
                                    const hasEmail = String(user.email || "").trim().length > 0;
                                    return (
                                        <div key={user.id} style={{ display: "grid", gridTemplateColumns: "2fr 1fr", padding: "10px 12px", borderTop: "1px solid #e5e7eb", alignItems: "center", opacity: hasEmail ? 1 : 0.55 }}>
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{getUserDisplayNameById(users, user.id)}</div>
                                                <div style={{ fontSize: 12, color: "#6b7280" }}>{hasEmail ? user.email : "Email non renseigné"}</div>
                                            </div>
                                            <select
                                                value={participantModes[user.id] || "NONE"}
                                                disabled={!hasEmail}
                                                onChange={(e) =>
                                                    setParticipantModes((prev) => {
                                                        const next = { ...prev };
                                                        if (e.target.value === "NONE") delete next[user.id];
                                                        else next[user.id] = e.target.value;
                                                        return next;
                                                    })
                                                }
                                            >
                                                <option value="NONE">Aucun</option>
                                                <option value="TO">TO</option>
                                                <option value="CC">CC</option>
                                            </select>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">Fermer</button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={handleSubmit}
                        disabled={loading || !date || !heure || !modeEntretien || !lieuEntretien}
                        type="button"
                    >
                        <RefreshCw size={15} /> Replanifier
                    </button>
                </div>
            </div>
        </div>
    );
}

function AnnulerEntretienModal({ open, onClose, candidat, entretien, onConfirm, loading }) {
    if (!open || !candidat || !entretien) return null;

    return (
        <div className="tcmSubModal" onMouseDown={onClose} role="dialog" aria-modal="true">
            <div className="tcmSubModal__panel tcmSubModal__panel--sm" onMouseDown={(e) => e.stopPropagation()}>
                <div className="tcmSubModal__header">
                    <div className="tcmSubModal__title">
                        <Ban size={17} />
                        Annuler l'entretien
                    </div>
                    <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                        <X size={17} />
                    </button>
                </div>

                <div className="tcmSubModal__body">
                    <div className="tcmTerminerContent">
                        <p className="tcmTerminerText">
                            Confirmez-vous l'annulation de l'entretien de{" "}
                            <strong>{candidat.candidatPrenom} {candidat.candidatNom}</strong> prévu le{" "}
                            <strong>{formatDate(entretien.date)}</strong> à <strong>{entretien.heure || "—"}</strong> ?
                        </p>
                        <p className="tcmTerminerSubText">Cette action enverra une annulation Outlook ICS.</p>
                    </div>
                </div>

                <div className="tcmSubModal__footer">
                    <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">Retour</button>
                    <button
                        className="tcmBtn tcmBtn--primary"
                        onClick={() => onConfirm("CANCEL_INTERVIEW", entretien, {})}
                        disabled={loading}
                        type="button"
                    >
                        <Ban size={15} /> Annuler
                    </button>
                </div>
            </div>
        </div>
    );
}

/* =============================================
   HELPER : badge couleur validation manager
============================================= */
const getValidationManagerBadge = (statut) => {
    const s = normalizeStatus(statut);
    let bg, color;
    if (s === "validée" || s === "validee" || s === "approuvée" || s === "approuvee") {
        bg = "#d1fae5"; color = "#065f46";
    } else if (s === "refusée" || s === "refusee" || s === "rejetée" || s === "rejetee") {
        bg = "#fee2e2"; color = "#991b1b";
    } else if (s === "en attente" || s === "en_attente" || s === "pending") {
        bg = "#fef3c7"; color = "#92400e";
    } else {
        bg = "#f3f4f6"; color = "#374151";
    }
    return (
        <span className="tcmStatutPill" style={{ background: bg, color }}>
            {statut || "—"}
        </span>
    );
};

/* =============================================
   MAIN : TraitementCandidatsModal
============================================= */
export default function TraitementCandidatsModal({ open, onClose, demande }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const safeDemande = demande || {};
    const demandeId = safeDemande.id;

    const candidaturesState = useSelector((state) => state.candidatures || {});
    const candidaturesRedux = candidaturesState.candidatures || [];
    const loading = !!candidaturesState.loading;
    const error = candidaturesState.error;

    const entretiensState = useSelector((state) => state.entretiens || {});
    const entretiensByCandidature = entretiensState.entretiensByCandidature || {};

    const offresSalarialesState = useSelector((state) => state.offresSalariales || {});
    const offreByCandidature = offresSalarialesState.offreByCandidature || {};
    const offresLoading = !!offresSalarialesState.loading;

    const userState = useSelector((state) => state.user || {});
    const users = userState.users || [];
    const currentUserId = Number(sessionStorage.getItem("uid") || 0);
    console.log("users", users);
    console.log("safeDemande", safeDemande);
    const buildCvUrl = (filename) => {
        if (!filename) return "#";
        if (/^https?:\/\//i.test(String(filename))) return filename;
        return `${normalizeApiBase()}/candidat-theque/cv/${encodeURIComponent(filename)}`;
    };

    const handleDownloadCv = async (candidat) => {
        const candidatId = getCandidatCvId(candidat);
        if (!candidatId || !getCandidatCvName(candidat)) {
            showSwal({
                icon: "warning",
                text: "Aucun CV disponible pour ce candidat",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        try {
            const response = await getBlobRequest({
                url: `${API_BASE_URL}/candidat-theque/${candidatId}/cv`,
                token: sessionStorage.getItem("token"),
                extraHeaders: {
                    uid: sessionStorage.getItem("uid"),
                    "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
                },
            });
            const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download =
                getFilenameFromContentDisposition(response.headers?.["content-disposition"]) ||
                getCandidatCvName(candidat) ||
                `cv-candidat-${candidatId}`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Erreur téléchargement CV :", error);
            showSwal({
                icon: "error",
                text: "Impossible de télécharger le CV.",
                timer: 2200,
                showConfirmButton: false,
            });
        }
    };

    const [candidats, setCandidats] = useState([]);
    const [requestedDemandeId, setRequestedDemandeId] = useState(null);

    const [filtreStatut, setFiltreStatut] = useState("TOUS");

    const statutsDisponibles = useMemo(() => {
        const set = new Set((candidaturesRedux || []).map((c) => c.statut).filter(Boolean));
        return Array.from(set);
    }, [candidaturesRedux]);

    const candidatsApprouves = useMemo(() => {
        if (filtreStatut === "TOUS") return candidaturesRedux || [];
        return (candidaturesRedux || []).filter(
            (c) => normalizeStatus(c.statut) === normalizeStatus(filtreStatut)
        );
    }, [candidaturesRedux, filtreStatut]);

    const buildCandidatRows = (source, previousRows) => {
        const localMap = new Map((previousRows || []).map((item) => [item.id, item]));
        return (source || []).map((item) => {
            const existing = localMap.get(item.id);
            const existingOffre = offreByCandidature[item.id];
            const existingOffreStatutTraitement =
                existingOffre?.statutOffre === "ACCEPTEE"
                    ? "Offre acceptée"
                    : existingOffre?.statutOffre === "REFUSEE"
                        ? "Offre refusée"
                        : "Offre envoyée";
            return {
                ...item,
                evaluation: existing?.evaluation || null,
                offreSalariale: existingOffre || existing?.offreSalariale || null,
                notesEntretien: existing?.notesEntretien || null,
                statutOffre: existingOffre?.statutOffre || existing?.statutOffre || null,
                statutTraitement: existing?.statutTraitement || (existingOffre ? existingOffreStatutTraitement : "En attente"),
            };
        });
    };

    const [planifierModal, setPlanifierModal] = useState({ open: false, candidat: null });
    const [planIAModal, setPlanIAModal] = useState({ open: false, candidat: null });
    const [notesModal, setNotesModal] = useState({ open: false, candidat: null });
    const [evaluerModal, setEvaluerModal] = useState({ open: false, candidat: null });
    const [offreModal, setOffreModal] = useState({ open: false, candidat: null });
    const [terminerModal, setTerminerModal] = useState({ open: false, candidat: null });
    const [historiqueModal, setHistoriqueModal] = useState({ open: false, candidat: null, entretiens: [] });
    const [replanifierModal, setReplanifierModal] = useState({ open: false, candidat: null, entretien: null });
    const [annulerEntretienModal, setAnnulerEntretienModal] = useState({ open: false, candidat: null, entretien: null });

    useEffect(() => {
        if (!open || !demandeId) return;
        setRequestedDemandeId(demandeId);
        dispatch(fetchCandidaturesByDemandeRequest(demandeId));
        dispatch(getAllUsersRequest());
    }, [dispatch, open, demandeId]);

    useEffect(() => {
        candidatsApprouves.forEach((c) => {
            dispatch(fetchEntretiensByCandidatureRequest(c.id));
            dispatch(fetchOffreSalarialeByCandidatureRequest(c.id));
        });
    }, [dispatch, candidatsApprouves]);

    useLayoutEffect(() => {
        setCandidats((prev) => buildCandidatRows(candidatsApprouves, prev));
    }, [candidatsApprouves, offreByCandidature]);

    useEffect(() => {
        setCandidats((prev) => {
            const localMap = new Map(prev.map((item) => [item.id, item]));
            return candidatsApprouves.map((item) => {
                const existing = localMap.get(item.id);
                const existingOffre = offreByCandidature[item.id];
                const existingOffreStatutTraitement =
                    existingOffre?.statutOffre === "ACCEPTEE"
                        ? "Offre acceptée"
                        : existingOffre?.statutOffre === "REFUSEE"
                            ? "Offre refusée"
                            : "Offre envoyée";
                return {
                    ...item,
                    evaluation: existing?.evaluation || null,
                    offreSalariale: existingOffre || existing?.offreSalariale || null,
                    notesEntretien: existing?.notesEntretien || null,
                    statutOffre: existingOffre?.statutOffre || existing?.statutOffre || null,
                    statutTraitement: existing?.statutTraitement || (existingOffre ? existingOffreStatutTraitement : "En attente"),
                };
            });
        });
    }, [candidatsApprouves, offreByCandidature]);

    useEffect(() => {
        const isAnyModalOpen =
            open ||
            planifierModal.open ||
            planIAModal.open ||
            notesModal.open ||
            evaluerModal.open ||
            offreModal.open ||
            terminerModal.open ||
            historiqueModal.open ||
            replanifierModal.open ||
            annulerEntretienModal.open;

        document.body.style.overflow = isAnyModalOpen ? "hidden" : "unset";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [
        open,
        planifierModal.open,
        planIAModal.open,
        notesModal.open,
        evaluerModal.open,
        offreModal.open,
        terminerModal.open,
        historiqueModal.open,
        replanifierModal.open,
        annulerEntretienModal.open,
    ]);

    const updateCandidat = (id, updates) => {
        setCandidats((prev) => prev.map((c) => (c.id === id ? { ...c, ...updates } : c)));
    };

    const getEntretienStats = (candidatureId) => {
        const list = entretiensByCandidature[candidatureId] || [];
        const tech = list.filter((e) => e.type === "TECHNIQUE").length;
        const rh = list.filter((e) => e.type === "RH").length;
        const hybride = list.filter((e) => e.type === "HYBRIDE").length;
        return { tech, rh, hybride, total: list.length };
    };

    const candidatsIds = candidats.map((item) => Number(item.id)).join(",");
    const candidatsApprouvesIds = candidatsApprouves.map((item) => Number(item.id)).join(",");
    const displayedCandidats =
        candidatsIds === candidatsApprouvesIds
            ? candidats
            : buildCandidatRows(candidatsApprouves, candidats);
    const isInitialCandidaturesLoading =
        !!open &&
        !!demandeId &&
        (requestedDemandeId !== demandeId || loading) &&
        displayedCandidats.length === 0;

    const handlePlanifierSave = (data) => {
        const c = planifierModal.candidat;
        if (!c) return;

        dispatch(
            createEntretienRequest({
                type: data.type,
                mode: data.mode,
                lieu: data.lieu,
                date: data.date,
                heure: data.heure,
                dureeMinutes: data.dureeMinutes,
                commentaire: data.commentaire,
                toParticipantIds: data.toParticipantIds || [],
                ccParticipantIds: data.ccParticipantIds || [],
                candidatureId: c.id,
            })
        );

        setTimeout(() => {
            dispatch(fetchEntretiensByCandidatureRequest(c.id));
        }, 300);

        updateCandidat(c.id, {
            statutTraitement: c.statutTraitement === "En attente" ? "En cours" : c.statutTraitement,
        });

        showSwal({
            icon: "success",
            text: `Entretien planifié pour ${c.candidatPrenom} ${c.candidatNom}`,
            timer: 1600,
            showConfirmButton: false,
        });
    };

    const handleNotesSave = (data) => {
        const c = notesModal.candidat;
        if (!c) return;

        updateCandidat(c.id, {
            notesEntretien: data,
        });

        showSwal({
            icon: "success",
            text: `Notes d'entretien enregistrées pour ${c.candidatPrenom} ${c.candidatNom}`,
            timer: 1600,
            showConfirmButton: false,
        });
    };

    const handleEvaluerSave = (data) => {
        const c = evaluerModal.candidat;
        if (!c) return;
        updateCandidat(c.id, {
            evaluation: data,
            statutTraitement: "Évalué",
        });
    };

    const handleOffreSave = (data) => {
        const c = offreModal.candidat;
        if (!c) return;

        const existingOffre = offreByCandidature[c.id] || c.offreSalariale;
        const payload = {
            ...data,
            candidatureId: c.id,
        };

        if (existingOffre?.id) {
            dispatch(updateOffreSalarialeRequest(existingOffre.id, payload));
        } else {
            dispatch(createOffreSalarialeRequest(payload));
        }

        const statutTraitement =
            data.statutOffre === "ACCEPTEE"
                ? "Offre acceptée"
                : data.statutOffre === "REFUSEE"
                    ? "Offre refusée"
                    : "Offre envoyée";

        updateCandidat(c.id, {
            offreSalariale: data,
            statutOffre: data.statutOffre,
            statutTraitement,
        });
    };

    const handleHistoriqueAction = (entretien, actionType) => {
        if (!historiqueModal.candidat || !entretien) return;
        if (actionType === "RESCHEDULE_INTERVIEW") {
            setReplanifierModal({ open: true, candidat: historiqueModal.candidat, entretien });
            return;
        }
        if (actionType === "CANCEL_INTERVIEW") {
            setAnnulerEntretienModal({ open: true, candidat: historiqueModal.candidat, entretien });
        }
    };

    const handleInterviewCalendarSubmit = (actionType, entretien, payload) => {
        if (!entretien?.id) return;

        if (actionType === "RESCHEDULE_INTERVIEW") {
            dispatch(
                updateEntretienRequest(
                    entretien.id,
                    {
                        type: payload.type || entretien.type,
                        mode: payload.mode,
                        lieu: payload.lieu,
                        date: payload.date,
                        heure: payload.heure,
                        dureeMinutes: payload.dureeMinutes,
                        commentaire: payload.commentaire,
                        toParticipantIds: payload.toParticipantIds || [],
                        ccParticipantIds: payload.ccParticipantIds || [],
                        candidatureId: replanifierModal.candidat?.id,
                    },
                    {
                        toParticipantIds: payload.toParticipantIds || [],
                        ccParticipantIds: payload.ccParticipantIds || [],
                    }
                )
            );
            setReplanifierModal({ open: false, candidat: null, entretien: null });
            return;
        }

        if (actionType === "CANCEL_INTERVIEW") {
            dispatch(sendInterviewCalendarRequest(entretien.id, actionType, {}));
            setAnnulerEntretienModal({ open: false, candidat: null, entretien: null });
        }
    };

    const handleTerminer = () => {
        const c = terminerModal.candidat;
        if (!c) return;

        updateCandidat(c.id, { statutTraitement: "Recruté ✓" });
        setTerminerModal({ open: false, candidat: null });

        showSwal({
            icon: "success",
            text: `Le recrutement de ${c.candidatPrenom} ${c.candidatNom} est finalisé.`,
            timer: 1800,
            showConfirmButton: false,
        });
    };

    /* const getStatutBadge = (statut) => {
        const map = {
            "En attente": { bg: "#fef3c7", color: "#92400e" },
            "En cours": { bg: "#dbeafe", color: "#1e40af" },
            "Évalué": { bg: "#e0e7ff", color: "#3730a3" },
            "Offre envoyée": { bg: "#d1fae5", color: "#065f46" },
            "Offre acceptée": { bg: "#d1fae5", color: "#065f46" },
            "Offre refusée": { bg: "#fee2e2", color: "#991b1b" },
            "Recruté ✓": { bg: "#d1fae5", color: "#065f46" },
        };
        const style = map[statut] || { bg: "#f3f4f6", color: "#374151" };

        return (
            <span className="tcmStatutPill" style={{ background: style.bg, color: style.color }}>
                {statut}
            </span>
        );
    }; */

    const getWorkflowStatutBadge = (statut, fallback = "En attente") => {
        if (fallback === "—" && isMissingStatut(statut)) {
            return <span className="tcmMuted">—</span>;
        }

        const raw = isMissingStatut(statut) ? fallback : String(statut).trim();
        const normalized = raw.toUpperCase();
        let bg = "#f3f4f6";
        let color = "#374151";
        let label = raw;

        if (["VALIDE", "VALIDEE", "ACCEPTE", "ACCEPTEE"].includes(normalized)) {
            bg = "#d1fae5";
            color = "#065f46";
        } else if (["REFUSE", "REFUSEE", "A_REVOIR", "REJETE", "REJETEE"].includes(normalized)) {
            bg = "#fee2e2";
            color = "#991b1b";
            label = "Refusé";
        } else if (["EN_ATTENTE", "A_COMPLETER", "NON_EVALUE", "NON_TRAITE", "ENVOYE"].includes(normalized)) {
            bg = "#fef3c7";
            color = "#92400e";
        }

        if (normalized === "ENVOYE") {
            label = "Envoyé";
        }

        return (
            <span className="tcmStatutPill" style={{ background: bg, color }}>
                {label}
            </span>
        );
    };

    if (!open) return null;

    return (
        <>
            <div className="tcmModal" onMouseDown={onClose} role="dialog" aria-modal="true">
                <div className="tcmModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                    <div className="tcmModal__header">
                        <div className="tcmModal__title">
                            <Users size={18} />
                            Traitement des candidats —{" "}
                            <span className="tcmModal__titlePoste">{getDisplayedPoste(safeDemande)}</span>
                        </div>
                        <button className="tcmIconBtn" onClick={onClose} aria-label="Fermer" type="button">
                            <X size={18} />
                        </button>
                    </div>

                    <div className="tcmModal__body">
                        <div className="tcmDemandeInfo">
                            <div className="tcmDemandeInfo__item">
                                <User size={14} />
                                <span>
                                    <strong>Demandeur :</strong> {safeDemande?.demandeurNom}
                                </span>
                            </div>

                            <div className="tcmDemandeInfo__item">
                                <Briefcase size={14} />
                                <span>
                                    <strong>Poste :</strong> {getDisplayedPoste(safeDemande)}
                                </span>
                            </div>

                            <div className="tcmDemandeInfo__item">
                                <Clock size={14} />
                                <span>
                                    <strong>Statut demande :</strong>{" "}
                                    <span className="tcmDemandeInfo__status">
                                        {safeDemande.statut || safeDemande.status || "—"}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", marginRight: 2 }}>
                                Validation manager :
                            </span>
                            {["TOUS", ...statutsDisponibles].map((s) => (
                                <button
                                    key={s}
                                    type="button"
                                    onClick={() => setFiltreStatut(s)}
                                    style={{
                                        padding: "4px 13px",
                                        borderRadius: 20,
                                        border: filtreStatut === s ? "2px solid #204385" : "1px solid #e5e7eb",
                                        background: filtreStatut === s ? "#204385" : "transparent",
                                        color: filtreStatut === s ? "#fff" : "#374151",
                                        fontSize: 12,
                                        fontWeight: 600,
                                        cursor: "pointer",
                                        transition: "all 0.15s",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {s === "TOUS" ? "Tous" : s}
                                    {s !== "TOUS" && (
                                        <span style={{ marginLeft: 5, opacity: 0.72, fontWeight: 400 }}>
                                            ({(candidaturesRedux || []).filter((c) => normalizeStatus(c.statut) === normalizeStatus(s)).length})
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="tcmSection__title">
                            <User size={15} />
                            Candidats ({displayedCandidats.length})
                        </div>

                        <div className="tcmTableWrap">
                            <table className="tcmTable">
                                <thead>
                                <tr>
                                    <th>Candidat</th>
                                    <th>Poste</th>
                                    <th>Niveau d'étude</th>
                                    <th>Validation manager</th>
                                    <th>Entretien Tech.</th>
                                    <th>Entretien RH</th>
                                    <th>Entretien Hybride</th>
                                    <th>Évaluation</th>
                                    <th>Statut offre </th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {loading || isInitialCandidaturesLoading ? (
                                    <tr>
                                        <td colSpan={10} className="tcmEmptyCell">
                                            Chargement...
                                        </td>
                                    </tr>
                                ) : displayedCandidats.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="tcmEmptyCell">
                                            Aucun candidat pour cette demande
                                        </td>
                                    </tr>
                                ) : (
                                    displayedCandidats.map((candidat) => {
                                        const stats = getEntretienStats(candidat.id);
                                        const statusNorm = normalizeStatus(candidat.statut);
                                        const isRejected = ["refusée", "refusee", "rejetée", "rejetee", "en " +
                                        "" +
                                        "attente"].includes(statusNorm);
                                        const isEvaluationRefused = isDecisionFinaleRefusee(candidat);
                                        const isOffreSalarialeEnabled = !isRejected && isDecisionFinaleValide(candidat);
                                        const offreFallback = isRejected || isEvaluationRefused ? "—" : "En attente";

                                        return (
                                            <tr key={candidat.id}>
                                                <td>
                                                    <div>
                                                        <div className="tcmStrong">
                                                            {candidat.candidatPrenom} {candidat.candidatNom}
                                                        </div>
                                                        <div className="tcmMuted">
                                                            {candidat.candidatEmail || "—"}
                                                        </div>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="tcmCellInline">
                                                        <Briefcase size={13} />
                                                        <span className="tcmStrong">
                                                            {getDisplayedPoste(candidat)}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <div className="tcmCellInline">
                                                        <GraduationCap size={13} />
                                                        <span className="tcmMuted">
                                                            {getDisplayedNiveauEtude(candidat)}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    {getValidationManagerBadge(candidat.statut)}
                                                </td>

                                                <td>
                                                    <span className="tcmEntretienBadge">
                                                        Tech: {stats.tech}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span className="tcmEntretienBadge">
                                                        RH: {stats.rh}
                                                    </span>
                                                </td>

                                                <td>
                                                    <span className="tcmEntretienBadge">
                                                        Hyb: {stats.hybride}
                                                    </span>
                                                </td>

                                                <td>
                                                    {getWorkflowStatutBadge(candidat.decisionFinale, isRejected ? "—" : "En attente")}
                                                </td>

                                                <td>{getWorkflowStatutBadge(getOffreSalarialeStatut(candidat), offreFallback)}</td>

                                                <td className="is-right">
                                                    <div className="tcmRowActions">
                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--calendar"
                                                            title={isRejected ? "Validation manager rejetée" : "Planifier un entretien"}
                                                            type="button"
                                                            onClick={() => setPlanifierModal({ open: true, candidat })}
                                                            disabled={isRejected}
                                                        >
                                                            <Calendar size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn"
                                                            title={isRejected ? "Validation manager rejetée" : "Plan d'entretien (IA)"}
                                                            type="button"
                                                            onClick={() => setPlanIAModal({ open: true, candidat })}
                                                            disabled={isRejected}
                                                        >
                                                            <Sparkles size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn"
                                                            title={isRejected ? "Validation manager rejetée" : "Notes d'entretien"}
                                                            type="button"
                                                            onClick={() => setNotesModal({ open: true, candidat })}
                                                            disabled={isRejected}
                                                        >
                                                            <FileText size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn"
                                                            title={isRejected ? "Validation manager rejetée" : "Historique des entretiens"}
                                                            type="button"
                                                            onClick={() =>
                                                                setHistoriqueModal({
                                                                    open: true,
                                                                    candidat,
                                                                    entretiens: entretiensByCandidature[candidat.id] || [],
                                                                })
                                                            }
                                                            disabled={isRejected}
                                                        >
                                                            <History size={15} />
                                                        </button>

                                                        <button
                                                            className="tcmIconBtn tcmIconBtn--star"
                                                            title="Évaluer le candidat"
                                                            type="button"
                                                            onClick={() =>
                                                                navigate(`/recrutement/evaluations/candidatures/${candidat.id}/fiche`, {
                                                                    state: {
                                                                        viewMode: "RH",
                                                                        returnToTraitementCandidatsDemandeId: demandeId,
                                                                    },
                                                                })
                                                            }
                                                            disabled={isRejected}

                                                        >
                                                            <Star size={15} />
                                                        </button>
                                                        <button
                                                            className={`tcmIconBtn tcmIconBtn--dollar ${
                                                                !isOffreSalarialeEnabled ? "tcmIconBtn--disabled" : ""
                                                            }`}
                                                            title={
                                                                isRejected
                                                                    ? "Validation manager rejetée"
                                                                    : isEvaluationRefused
                                                                        ? "Évaluation refusée"
                                                                        : isOffreSalarialeEnabled
                                                                        ? "Offre salariale"
                                                                        : "Offre salariale disponible après décision finale VALIDE"
                                                            }
                                                            type="button"
                                                            onClick={() => {
                                                                if (!isOffreSalarialeEnabled) return;
                                                                setOffreModal({ open: true, candidat });
                                                            }}
                                                            disabled={!isOffreSalarialeEnabled}
                                                        >
                                                            <DollarSign size={15} />
                                                        </button>

                                                        <a
                                                            className="cvtIconBtn cvtIconBtn--download"
                                                            href={buildCvUrl(candidat.cvDocument)}
                                                            download
                                                            title="Télécharger le CV"
                                                            aria-label="Télécharger le CV"
                                                        >
                                                            <Download size={16} />
                                                        </a>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                            </table>
                        </div>

                        {!!error && (
                            <div className="tcmWarning" style={{ marginTop: 10 }}>
                                {String(error)}
                            </div>
                        )}
                    </div>

                    <div className="tcmModal__footer">
                        <button className="tcmBtn tcmBtn--ghost" onClick={onClose} type="button">
                            Fermer
                        </button>
                    </div>
                </div>
            </div>

            <PlanifierEntretienModal
                open={planifierModal.open}
                onClose={() => setPlanifierModal({ open: false, candidat: null })}
                candidat={planifierModal.candidat}
                onSave={handlePlanifierSave}
                users={users}
                currentUserId={currentUserId}
            />

            <PlanEntretienIAModal
                open={planIAModal.open}
                onClose={() => setPlanIAModal({ open: false, candidat: null })}
                candidat={planIAModal.candidat}
            />

            <NotesEntretienModal
                open={notesModal.open}
                onClose={() => setNotesModal({ open: false, candidat: null })}
                candidat={notesModal.candidat}
                initialData={notesModal.candidat?.notesEntretien}
                onSave={handleNotesSave}
            />

            <HistoriqueEntretiensModal
                open={historiqueModal.open}
                onClose={() => setHistoriqueModal({ open: false, candidat: null, entretiens: [] })}
                candidat={historiqueModal.candidat}
                entretiens={historiqueModal.entretiens}
                users={users}
                onCalendarAction={handleHistoriqueAction}
            />

            <ReplanifierEntretienModal
                open={replanifierModal.open}
                onClose={() => setReplanifierModal({ open: false, candidat: null, entretien: null })}
                candidat={replanifierModal.candidat}
                entretien={replanifierModal.entretien}
                users={users}
                currentUserId={currentUserId}
                onSubmit={handleInterviewCalendarSubmit}
                loading={entretiensState.loading}
            />

            <AnnulerEntretienModal
                open={annulerEntretienModal.open}
                onClose={() => setAnnulerEntretienModal({ open: false, candidat: null, entretien: null })}
                candidat={annulerEntretienModal.candidat}
                entretien={annulerEntretienModal.entretien}
                onConfirm={handleInterviewCalendarSubmit}
                loading={entretiensState.loading}
            />

            <EvaluerModal
                open={evaluerModal.open}
                onClose={() => setEvaluerModal({ open: false, candidat: null })}
                candidat={evaluerModal.candidat}
                onSave={handleEvaluerSave}
            />

            <OffreSalarialeModal
                open={offreModal.open}
                onClose={() => setOffreModal({ open: false, candidat: null })}
                candidat={offreModal.candidat}
                existingOffre={
                    offreModal.candidat
                        ? (offreByCandidature[offreModal.candidat.id] || offreModal.candidat.offreSalariale || null)
                        : null
                }
                loading={offresLoading}
                onSave={handleOffreSave}
            />

            <TerminerModal
                open={terminerModal.open}
                onClose={() => setTerminerModal({ open: false, candidat: null })}
                candidat={terminerModal.candidat}
                onConfirm={handleTerminer}
            />
        </>
    );
}
